import { useEffect, useRef } from 'react';

/**
 * Keeps an uncontrolled input in step with a value that can change elsewhere,
 * without remounting it.
 *
 * The problem this replaces: several numeric fields were written as
 * `defaultValue={x}` plus `key={`something-${x}`}`. The key was there for a real
 * reason — an uncontrolled input ignores a changed `defaultValue`, so without it
 * a reset or a recalculation elsewhere on the page would never reach the field.
 * But because `x` is also what the input's own `onChange` writes, every accepted
 * keystroke changed the key, and React unmounted the input and mounted a new one
 * in its place. The cursor left the field mid-word, so the field took one
 * character per click.
 *
 * Making these controlled is not the fix either: `parseFloat` collapses the
 * half-typed states a decimal field has to pass through, so `70.` immediately
 * re-renders as `70` and the user cannot type a decimal point at all.
 *
 * So the input stays uncontrolled — free to hold whatever the user is partway
 * through typing — and this hook writes the DOM value only when the incoming
 * value genuinely differs and the user is not in the field. Typing is never
 * interrupted; an external change still lands.
 */
export function useExternalValueSync<T extends HTMLInputElement | HTMLTextAreaElement>(
  value: string | number
): React.RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Never yank the field out from under someone mid-edit: their in-progress
    // text is the more recent intent.
    if (document.activeElement === el) return;
    const next = value === null || value === undefined ? '' : String(value);
    if (el.value !== next) el.value = next;
  }, [value]);

  return ref;
}
